import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';

interface CollectivePost {
  id: string;
  title: string;
  category: 'sop' | 'prompt' | 'case-study' | 'template' | 'legal';
  department: string;
  author: string;
  summary: string;
  content: string;
  tags: string[];
  likes: number;
  hasLiked?: boolean;
  created_at: string;
}

export function CollectiveFeedPage() {
  const { tenant } = useAuth();
  const [posts, setPosts] = useState<CollectivePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);

  // Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    category: 'sop' as CollectivePost['category'],
    department: 'Khối Kỹ Thuật',
    summary: '',
    content: '',
    tags: ''
  });

  useEffect(() => {
    loadFeed();
  }, []);

  const loadFeed = async () => {
    setLoading(true);
    try {
      const res = await api.getCollectiveFeed();
      setPosts(res.posts || []);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId: string) => {
    try {
      const res = await api.likeCollectivePost(postId);
      if (res.posts) {
        setPosts(res.posts);
      } else {
        setPosts(prev => prev.map(p => {
          if (p.id === postId) {
            const hasLiked = !p.hasLiked;
            return { ...p, hasLiked, likes: hasLiked ? p.likes + 1 : Math.max(0, p.likes - 1) };
          }
          return p;
        }));
      }
    } catch (e: any) {
      console.error(e);
    }
  };

  const handleImportToOperations = async (post: CollectivePost) => {
    try {
      const currentOps = await api.getOperations().catch(() => ({}));
      // Append to the relevant tab (sop or tech or business)
      const tabKey = post.category === 'sop' ? 'sop' : post.category === 'prompt' ? 'tech' : 'business';
      const existingText = currentOps[tabKey] || '';
      const snippet = `\n\n---\n### 📥 [Collective] ${post.title}\n*Được chia sẻ bởi: ${post.author}*\n\n${post.content}\n`;
      const updatedOps = { ...currentOps, [tabKey]: existingText + snippet };
      await api.saveOperations(updatedOps);
      alert(`🎉 Đã nạp bài học "${post.title}" vào tab ${tabKey.toUpperCase()} của Operations Hub thành công!`);
    } catch (e: any) {
      alert('Không thể nạp vào Operations Hub: ' + e.message);
    }
  };

  const handleCopyMarkdown = (content: string) => {
    navigator.clipboard.writeText(content);
    alert('📋 Đã sao chép nội dung Markdown vào clipboard!');
  };

  const handleCreatePost = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      alert('Vui lòng điền tiêu đề và nội dung bài học!');
      return;
    }
    setSubmitting(true);
    try {
      const tagList = form.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
      const res = await api.createCollectivePost({
        title: form.title.trim(),
        category: form.category,
        department: form.department.trim(),
        summary: form.summary.trim() || form.content.slice(0, 150) + '...',
        content: form.content.trim(),
        tags: tagList
      });
      if (res.post) {
        setPosts([res.post, ...posts]);
      } else {
        await loadFeed();
      }
      setShowCreateModal(false);
      setForm({
        title: '',
        category: 'sop',
        department: 'Khối Kỹ Thuật',
        summary: '',
        content: '',
        tags: ''
      });
      alert('🚀 Đã chia sẻ tri thức thành công tới toàn bộ tổ chức!');
    } catch (e: any) {
      alert('Lỗi: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryBadge = (cat: CollectivePost['category']) => {
    switch (cat) {
      case 'sop': return <span className="badge badge--blue">💡 Quy Trình SOP</span>;
      case 'prompt': return <span className="badge badge--purple">🤖 Prompt AI</span>;
      case 'case-study': return <span className="badge badge--green">📚 Case Study</span>;
      case 'template': return <span className="badge badge--cyan">📋 Biểu Mẫu</span>;
      case 'legal': return <span className="badge badge--red">⚖️ Pháp Lý</span>;
    }
  };

  const filteredPosts = posts.filter(p => {
    const matchCat = selectedCategory === 'all' || p.category === selectedCategory;
    const matchSearch = !searchQuery || 
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase())) ||
      p.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '16px' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang nạp Kho Tri Thức Tập Thể (Collective Intelligence)...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '16px', overflowY: 'auto', paddingRight: '4px' }}>

      {/* Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 className="page-title">🌐 Collective Intelligence Feed</h1>
            <span className="badge badge--blue" style={{ fontSize: '11px', padding: '3px 10px' }}>
              Kho Đúc Kết Tri Thức Nội Bộ
            </span>
          </div>
          <p className="page-subtitle">
            Học tập và kế thừa kinh nghiệm thực chiến từ các phòng ban: SOP chuẩn hóa, Prompt Master, Case Study và Biểu mẫu
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button className="btn btn--primary" onClick={() => setShowCreateModal(true)}>
            ➕ Chia Sẻ Tri Thức Mới
          </button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-card)', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: 'Tất cả' },
            { id: 'sop', label: '💡 Quy trình SOP' },
            { id: 'prompt', label: '🤖 Prompt AI' },
            { id: 'case-study', label: '📚 Case Study' },
            { id: 'template', label: '📋 Biểu Mẫu' },
            { id: 'legal', label: '⚖️ Pháp Lý' }
          ].map(c => (
            <button
              key={c.id}
              className={`btn btn--sm ${selectedCategory === c.id ? 'btn--primary' : 'btn--secondary'}`}
              onClick={() => setSelectedCategory(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>

        <input
          className="input"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="🔍 Tìm kiếm theo tiêu đề, tag, tác giả..."
          style={{ width: '280px', fontSize: '12.5px', padding: '6px 12px' }}
        />
      </div>

      {/* Feed List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {filteredPosts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-muted)' }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>📭</span>
            <p>Chưa có bài học nào trong danh mục này hoặc không khớp với từ khóa tìm kiếm.</p>
          </div>
        ) : (
          filteredPosts.map(post => {
            const isExpanded = expandedPostId === post.id;
            return (
              <div
                key={post.id}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transition: 'border-color 0.2s'
                }}
              >
                {/* Meta Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {getCategoryBadge(post.category)}
                    <span className="badge badge--gray" style={{ fontSize: '11px' }}>🏢 {post.department}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Tác giả: <strong style={{ color: '#e2e8f0' }}>{post.author}</strong>
                    </span>
                  </div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {new Date(post.created_at).toLocaleDateString('vi-VN')}
                  </span>
                </div>

                {/* Title & Summary */}
                <div>
                  <h2
                    style={{ margin: '0 0 6px 0', fontSize: '16.5px', fontWeight: 600, color: '#f8fafc', cursor: 'pointer' }}
                    onClick={() => setExpandedPostId(isExpanded ? null : post.id)}
                  >
                    {post.title}
                  </h2>
                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {post.summary}
                  </p>
                </div>

                {/* Expanded Full Content */}
                {isExpanded && (
                  <div style={{
                    background: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '8px',
                    padding: '16px',
                    fontSize: '13px',
                    lineHeight: 1.6,
                    color: '#f1f5f9',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}>
                    {post.content}
                  </div>
                )}

                {/* Tags & Action Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  {/* Tag Chips */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {post.tags.map((t, idx) => (
                      <span
                        key={idx}
                        onClick={() => setSearchQuery(t)}
                        style={{
                          fontSize: '11px',
                          color: '#38bdf8',
                          background: 'rgba(56, 189, 248, 0.1)',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        #{t}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn--secondary btn--sm"
                      onClick={() => setExpandedPostId(isExpanded ? null : post.id)}
                    >
                      {isExpanded ? '▲ Thu gọn' : '▼ Xem chi tiết'}
                    </button>
                    <button
                      className="btn btn--secondary btn--sm"
                      onClick={() => handleLike(post.id)}
                      style={{
                        background: post.hasLiked ? 'rgba(239, 68, 68, 0.2)' : undefined,
                        borderColor: post.hasLiked ? '#f87171' : undefined,
                        color: post.hasLiked ? '#f87171' : undefined
                      }}
                    >
                      ❤️ Hữu ích ({post.likes})
                    </button>
                    <button
                      className="btn btn--secondary btn--sm"
                      onClick={() => handleCopyMarkdown(post.content)}
                      title="Sao chép Markdown"
                    >
                      📋 Copy
                    </button>
                    <button
                      className="btn btn--primary btn--sm"
                      onClick={() => handleImportToOperations(post)}
                      title="Nạp thẳng quy trình này vào Operations Hub của bạn"
                    >
                      📥 Nạp vào Operations Hub
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* MODAL: CREATE POST */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '640px' }}>
            <div className="modal__header">
              <h2 className="modal__title">➕ Chia Sẻ Tri Thức Doanh Nghiệp</h2>
              <button className="modal__close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="modal__body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Tiêu Đề Bài Học / SOP / Prompt</label>
                <input
                  className="input"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="VD: Quy trình 5 bước nghiệm thu hồ sơ thanh toán nhà thầu"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Phân Loại</label>
                  <select
                    className="input"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value as any })}
                  >
                    <option value="sop">💡 Quy Trình SOP</option>
                    <option value="prompt">🤖 Prompt AI</option>
                    <option value="case-study">📚 Case Study Dự Án</option>
                    <option value="template">📋 Biểu Mẫu / Template</option>
                    <option value="legal">⚖️ Pháp Lý & Tuân Thủ</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Phòng Ban Đóng Góp</label>
                  <input
                    className="input"
                    value={form.department}
                    onChange={e => setForm({ ...form, department: e.target.value })}
                    placeholder="VD: Phòng Pháp Chế"
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Tóm Tắt Ngắn (Summary)</label>
                <input
                  className="input"
                  value={form.summary}
                  onChange={e => setForm({ ...form, summary: e.target.value })}
                  placeholder="Mô tả súc tích giá trị của bài học hoặc biểu mẫu..."
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Nội Dung Chi Tiết (Hỗ trợ Markdown)</label>
                <textarea
                  className="input"
                  rows={8}
                  value={form.content}
                  onChange={e => setForm({ ...form, content: e.target.value })}
                  placeholder="## Hướng dẫn chi tiết...&#10;1. Bước 1...&#10;2. Bước 2..."
                  style={{ fontFamily: 'var(--font-mono)', fontSize: '12.5px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Thẻ Tags (phân tách bởi dấu phẩy)</label>
                <input
                  className="input"
                  value={form.tags}
                  onChange={e => setForm({ ...form, tags: e.target.value })}
                  placeholder="legal, sop, risk, hop-dong"
                />
              </div>
            </div>
            <div className="modal__footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn--secondary" onClick={() => setShowCreateModal(false)}>Hủy</button>
              <button className="btn btn--primary" onClick={handleCreatePost} disabled={submitting}>
                {submitting ? 'Đang chia sẻ...' : '🚀 Công Bố Cho Tổ Chức'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
